import { useState, useEffect } from "react";
import io, { Socket } from "socket.io-client";
import AccelerometerCharts from "./AccelerometerCharts";
import "./App.css";

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || "http://localhost:3000";

function App() {
  const [accelerometerData, setAccelerometerData] = useState<any | null>(null);
  const [view, setView] = useState<"upload" | "charts">("upload");
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<{
    connected: boolean;
    lastUpdate: Date | null;
  }>({
    connected: false,
    lastUpdate: null,
  });
  const [isRealTime, setIsRealTime] = useState(false);

  // Connessione WebSocket per aggiornamenti real-time
  useEffect(() => {
    if (!isRealTime) return;

    console.log("🔌 Connessione WebSocket a:", SOCKET_URL);

    const newSocket = io(SOCKET_URL, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    newSocket.on("connect", () => {
      console.log("✅ Connesso al server");
      setConnectionStatus((prev) => ({ ...prev, connected: true }));
    });

    newSocket.on("disconnect", () => {
      console.log("❌ Disconnesso dal server");
      setConnectionStatus((prev) => ({ ...prev, connected: false }));
    });

    newSocket.on("accelerometerData", (data: any) => {
      console.log(`📊 Ricevuti ${data.data.length} campioni via WebSocket`);
      setAccelerometerData(data);
      setConnectionStatus((prev) => ({ ...prev, lastUpdate: new Date() }));
    });

    newSocket.on("connect_error", (error: any) => {
      console.error("❌ Errore di connessione:", error.message);
    });

    setSocket(newSocket);

    return () => {
      console.log("🔌 Chiusura connessione WebSocket");
      newSocket.close();
    };
  }, [isRealTime]);

  const handleStartRealTime = () => {
    setView("charts");
    setIsRealTime(true);
    console.log("🔄 Modalità real-time attivata");
  };

  const handleBackToUpload = () => {
    setView("upload");
    setIsRealTime(false);
    if (socket) {
      socket.close();
    }
  };

  const getTimeSinceUpdate = () => {
    if (!connectionStatus.lastUpdate) return "Mai";
    const seconds = Math.floor((Date.now() - connectionStatus.lastUpdate.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s fa`;
    return `${Math.floor(seconds / 60)}m fa`;
  };

  return (
    <div className="app">
      <header className="header">
        <h1>🌊 WitMotion Dashboard</h1>
        <div className="header-actions">
          {view === "charts" && (
            <>
              {isRealTime && (
                <div className={`status-badge ${connectionStatus.connected ? "connected" : "disconnected"}`}>
                  {connectionStatus.connected ? "🟢 Real-Time" : "🔴 Disconnesso"}
                </div>
              )}
              <button className="back-button" onClick={handleBackToUpload}>
                ← Indietro
              </button>
            </>
          )}
        </div>
      </header>

      <main className="main-content">
        {view === "upload" ? (
          <div className="upload-view">
            <div className="welcome-section">
              <h2>Analisi Dati Accelerometro</h2>
              <p className="subtitle">
                Carica un file di dati WitMotion o visualizza dati in tempo reale
              </p>
            </div>

            <div className="mode-selector">
              <button className="mode-button primary" onClick={handleStartRealTime}>
                🔴 Modalità Real-Time
                <span className="mode-description">Connetti al simulatore e visualizza dati live</span>
              </button>
            </div>

            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">📈</div>
                <h3>Accelerazione 3D</h3>
                <p>Visualizza l'accelerazione su 3 assi in unità g</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">🔄</div>
                <h3>Velocità Angolare</h3>
                <p>Monitora la rotazione del giroscopio</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">📐</div>
                <h3>Orientamento</h3>
                <p>Angoli Roll, Pitch e Yaw in tempo reale</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="charts-view">
            {accelerometerData && (
              <>
                <div className="file-info-banner">
                  <div className="file-info-content">
                    <span className="file-icon">{isRealTime ? "🔴" : "📄"}</span>
                    <div className="file-details">
                      <strong>
                        {isRealTime ? "Live Data Stream" : accelerometerData.filename}
                      </strong>
                      <span className="file-meta">
                        {accelerometerData.count} campioni • {accelerometerData.headers.length} colonne
                        {isRealTime && ` • Aggiornato ${getTimeSinceUpdate()}`}
                      </span>
                    </div>
                  </div>
                </div>
                <AccelerometerCharts data={accelerometerData.data} />
              </>
            )}
            {!accelerometerData && isRealTime && (
              <div className="loading-real-time">
                <div className="spinner"></div>
                <p>🔄 Connessione al simulatore...</p>
                <p className="hint">Assicurati che il server e il simulatore siano in esecuzione</p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
